import { useMemo, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import NewsRow from "@/components/feature/NewsRow";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { newsList, newsTags } from "@/mocks/news";

const PAGE_SIZE = 5;

export default function NewsList() {
  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState<"new" | "hot">("new");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = newsList.map((item, i) => ({
      ...item,
      heat: (item.title.length * 7 + i * 13) % 100,
    }));
    if (tag !== "全部") {
      list = list.filter((n) => n.tags.includes(tag));
    }
    list.sort((a, b) => {
      if (sort === "hot") return b.heat - a.heat;
      return a.date < b.date ? 1 : -1;
    });
    return list;
  }, [tag, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const current = Math.min(page, Math.max(totalPages, 1));
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const reset = () => {
    setTag("全部");
    setSort("new");
    setPage(1);
  };

  const changeTag = (t: string) => {
    setTag(t);
    setPage(1);
  };

  const changeSort = (s: "new" | "hot") => {
    setSort(s);
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        title="动漫资讯"
        description="汇集最新的动漫新闻动态、活动预告与行业访谈，助你第一时间掌握圈子里的新鲜事。"
        crumbs={[{ label: "动漫资讯" }]}
        icon="ri-newspaper-line"
      />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm font-medium text-foreground-600">标签：</span>
            {["全部", ...newsTags].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => changeTag(t)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  tag === t
                    ? "bg-accent-500 text-white"
                    : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground-600">排序：</span>
            <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
              <button
                type="button"
                onClick={() => changeSort("new")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  sort === "new" ? "bg-primary-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                }`}
              >
                最新
              </button>
              <button
                type="button"
                onClick={() => changeSort("hot")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  sort === "hot" ? "bg-primary-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                }`}
              >
                最热
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
          <span>
            共 <strong className="text-foreground-900">{filtered.length}</strong> 条资讯
          </span>
          {(tag !== "全部" || sort !== "new") && (
            <button
              type="button"
              onClick={reset}
              className="flex cursor-pointer items-center gap-1 text-accent-600 hover:text-accent-700"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-4">
          {pageItems.length > 0 ? (
            pageItems.map((item) => <NewsRow key={item.id} item={item} />)
          ) : (
            <EmptyState
              title="暂无符合条件的资讯，您可以调整筛选条件"
              description="试试切换其他标签，或查看全部最新资讯。"
              actionLabel="重置筛选条件"
              onAction={reset}
            />
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-8">
            <Pagination current={current} total={totalPages} onChange={setPage} />
          </div>
        )}
      </section>
    </div>
  );
}